/**
 * Marketing-only price display: static, export-friendly list of ingredients with product and price.
 * Used only on the /dev/marketing page for ad export. Supports removing ingredients; total updates accordingly.
 */
import React from 'react';

function formatAmount(amount, unit) {
  const n = parseFloat(amount);
  if (n == null || isNaN(n)) return '';
  const u = (unit || '').trim();
  if (u === 'other' || !u) return `${n}`;
  return `${Number(n) === n && n % 1 === 0 ? n : n.toFixed(2)} ${u}`;
}

function formatExcess(excess, excessUnit) {
  if (excess == null || Number(excess) <= 0) return null;
  const num = parseFloat(excess);
  const u = (excessUnit || 'g').toLowerCase();
  if (u === 'g' && num >= 1000) return `${(num / 1000).toFixed(1)}kg`;
  if (u === 'g') return `${Math.round(num)}g`;
  if (u === 'ml' && num >= 1000) return `${(num / 1000).toFixed(1)}L`;
  if (u === 'ml') return `${Math.round(num)}ml`;
  return `${num.toFixed(1)}${u}`;
}

export default function MarketingPriceDetails({
  ingredients = [],
  ingredientPrices = {},
  totalCost,
  excludedIngredientNames = [],
  onRemoveIngredient,
  onAddBackIngredient
}) {
  const excludedSet = new Set(excludedIngredientNames);
  const includedIngredients = ingredients.filter((ing) => !excludedSet.has(ing.name));
  const excludedIngredients = ingredients.filter((ing) => excludedSet.has(ing.name));
  const canRemove = typeof onRemoveIngredient === 'function';

  return (
    <div className="marketing-price-details">
      {totalCost != null && (
        <div className="marketing-total-cost">
          <span className="marketing-cost-label">Total cost:</span>
          <span className="marketing-cost-amount">R{Number(totalCost).toFixed(2)}</span>
        </div>
      )}
      <div className="marketing-ingredient-grid">
        {includedIngredients.map((ingredient, index) => {
          const data = ingredientPrices[ingredient.name];
          const brand = data?.selectedBrand || data?.availableBrands?.[0];
          if (!brand) return null;

          const amountStr = formatAmount(ingredient.amount, ingredient.unit === 'other' ? ingredient.customUnit : ingredient.unit);
          const price = parseFloat(brand.cost);
          const packageInfo = brand.packageInfo || null;
          const leftover = formatExcess(brand.excess, brand.excessUnit);

          return (
            <div key={`${ingredient.name}-${index}`} className="marketing-ingredient-card">
              {canRemove && (
                <button
                  type="button"
                  className="marketing-ingredient-remove"
                  onClick={() => onRemoveIngredient(ingredient.name)}
                  title="Remove from list"
                  aria-label={`Remove ${ingredient.name}`}
                >
                  ×
                </button>
              )}
              {brand.imageUrl && (
                <div className="marketing-ingredient-photo-wrap">
                  <img
                    src={brand.imageUrl}
                    alt={brand.name || ingredient.name}
                    className="marketing-ingredient-photo"
                  />
                </div>
              )}
              <div className="marketing-ingredient-card-body">
                <div className="marketing-ingredient-name">{ingredient.name}</div>
                <div className="marketing-ingredient-amount">{amountStr}</div>
                <div className="marketing-product-line">
                  {brand.name && <span className="marketing-product-name">{brand.name}</span>}
                  {brand.brand && <span className="marketing-product-store"> {brand.brand}</span>}
                </div>
                <div className="marketing-price-line">
                  <span className="marketing-price-label">Price</span>
                  <span className="marketing-price-value">R{price.toFixed(2)}</span>
                </div>
                <div className="marketing-amount-needed">Amount needed: {amountStr}</div>
                {packageInfo && (
                  <div className="marketing-package-size">Package size: {packageInfo}</div>
                )}
                {leftover && (
                  <div className="marketing-leftover">Leftover: {leftover}</div>
                )}
              </div>
            </div>
          );
        })}
      </div>
      {excludedIngredients.length > 0 && typeof onAddBackIngredient === 'function' && (
        <div className="marketing-removed-section">
          <div className="marketing-removed-title">Removed (click to add back)</div>
          <div className="marketing-removed-tags">
            {excludedIngredients.map((ing) => (
              <button
                key={ing.name}
                type="button"
                className="marketing-removed-tag"
                onClick={() => onAddBackIngredient(ing.name)}
              >
                {ing.name} +
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
